














































































import debounce from 'lodash/debounce';
import { mapState, mapActions } from 'vuex';
import {
  getPromoCampaignsParticipants,
  getPromoCampaignsParticipantsReward,
  giveReward,
  removeReward
} from '@/api/PromoCampaign';
import { errorToastMessage } from '@/helpers/errorToastMessage';
import type { AxiosError } from 'axios';
import type {
  PromoCampainPartisipanstSearchParams,
  PromoParticipantsListResponse,
  PromoParticipantData
} from '@/api/schema';
import DatePickerWithDialog from '@/components/DatePickerWithDialog.vue';
import ConfirmationDialog from '@/components/ConfirmationDialog.vue';
import StatsCard from '@/components/StatsCard.vue';

const defaultOptions = {
  page: 1,
  itemsPerPage: 10,
  sortDesc: [false]
};

export default {
  name: 'PromoViewParticipantsList',
  components: {
    ConfirmationDialog,
    DatePickerWithDialog,
    StatsCard
  },
  data: (): unknown => ({
    operatorUserId: null,
    showUserDeleteDialogData: null,
    userDeleteDialogData: {
      header: '',
      okText: 'Yes',
      cancelText: 'No',
      handler: null
    },
    reward: 0,
    options: { ...defaultOptions },
    itemsPerPage: [10, 20, 50],
    loading: false,
    participants: [],
    total: 0,
    dates: [],
    headers: [
      {
        text: 'OPERATOR USER ID',
        value: 'operatorUserId',
        sortable: false
      },
      {
        text: 'ACCRUAL METHOD',
        value: 'method',
        sortable: false
      },
      {
        text: 'DATE',
        value: 'createdAt'
      },
      {
        text: '',
        value: 'actions',
        sortable: false
      }
    ]
  }),
  computed: {
    ...mapState('Promo', ['selectedCampaign', 'dialog']),
    ...mapState('TokensInfo', ['tokenPrice']),
    currentPage(): number {
      return this.options.page - 1;
    },
    isActive(): boolean {
      return this.selectedCampaign?.status === 'ACTIVE';
    },
    isCashbackReward(): boolean {
      return (
        !!this.selectedCampaign?.dailyCashbackReward ||
        !!this.selectedCampaign?.weeklyCashbackReward
      );
    }
  },
  watch: {
    'dialog.participantsList'(show: boolean): void {
      if (show && this.selectedCampaign) {
        this.operatorUserId = null;
        this.updateParticipantsList();
        return;
      }
      this.participants = [];
    },
    options: {
      async handler(): Promise<void> {
        await this.updateParticipantsList();
      },
      deep: true
    },
    dates: 'updateParticipantsList',
    operatorUserId: debounce(function (newValue: string, oldValue: string) {
      if (newValue?.length >= 3 || oldValue?.length >= 3) {
        this.updateParticipantsList();
      }
    }, 500)
  },
  methods: {
    ...mapActions('Promo', ['showParticipantsDialog']),
    clearAction(): void {
      this.dates = [];
    },
    closeAction(): void {
      this.showParticipantsDialog({ status: false });
    },
    async addToOwnList(): Promise<void> {
      if (!this.operatorUserId || this.operatorUserId?.length < 3) return;

      await giveReward(this.selectedCampaign?.id, this.operatorUserId)
        .then(() => {
          this.forceUpdate();
          this.$toast.success(
            `User has been successfully added to ${this.selectedCampaign.name}.`
          );
        })
        .catch(() => {
          this.$toast.success(
            `User has not been added to ${this.selectedCampaign.name} due error, please provide correct ID`
          );
        });
    },
    showDeleteUserDialog(item: PromoParticipantData): void {
      this.showUserDeleteDialogData = true;
      this.userDeleteDialogData.header = `Are you sure to delete user ${item.operatorUserId} from campaign ${this.selectedCampaign.name}?`;
      this.userDeleteDialogData.handler = async () => {
        await removeReward(
          this.selectedCampaign?.id,
          item.operatorUserId
        ).then(this.forceUpdate);
      };
    },
    onUserDeleteDialogClosed(): void {
      this.showUserDeleteDialogData = false;
    },
    onUserDeleted(): void {
      this.showUserDeleteDialogData = false;
      this.userDeleteDialogData.handler?.();
    },
    forceUpdate(): void {
      this.operatorUserId = '';
      this.$nextTick(this.updateParticipantsList);
    },
    updateParticipantsList(): void {
      const { id } = this.selectedCampaign || {};
      const [from, to] = this.dates;
      if (!id || this.loading) return;

      const searchParams: PromoCampainPartisipanstSearchParams = {
        page: this.currentPage,
        size: this.options.itemsPerPage,
        direction: this.options.sortDesc[0] ? 'ASC' : 'DESC',
        from,
        to,
        operatorUserId:
          this.operatorUserId?.length >= 3 ? this.operatorUserId : null
      };

      this.loading = true;

      Promise.all([
        getPromoCampaignsParticipants(searchParams, id),
        getPromoCampaignsParticipantsReward(from, to, id)
      ])
        .then((data: [PromoParticipantsListResponse, number]): void => {
          this.setParticipantsData(data[0]);
          this.reward = data[1];
        })
        .catch((error: AxiosError): void => {
          errorToastMessage(error);
        })
        .finally((): void => {
          this.loading = false;
        });
    },
    setParticipantsData(data: PromoParticipantsListResponse): void {
      this.participants = data.content;
      this.total = data.totalElements;
    }
  }
};
